import { useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Button from '@raydiant/raydial/components/Button';
import Dialog, {
  DialogHeader,
  DialogHeading,
} from '@raydiant/raydial/components/Dialog';
import Modal from '@raydiant/raydial/components/Modal';
import ComboBox, { ComboBoxItem } from '@raydiant/raydial/components/ComboBox';
import Pill from '@raydiant/raydial/components/Pill';
import { User } from '@raydiant/raydial/components/Icons';
import {
  LocationACL,
  ResourceACL,
  ResourceGrant,
  Theme,
} from '@raydiant/api-client-js';
import { useToastState } from '@raydiant/raydial/components/Toast';
import { selectUserProfile } from '../../../selectors/user';
import useCreateResourceACLV5 from '../../../hooks/useCreateResourceACLV5';
import useUpdateResourceACLV5 from '../../../hooks/useUpdateResourceACLV5';
import useDeleteResourceACLV5 from '../../../hooks/useDeleteResourceACLV5';
import * as themeActions from '../../../actions/themes';
import {
  getResourceACLToLocationACL,
  selectItemToGrants,
} from '../../LocationsPage/utils';
import { GrantOption } from '../../LocationsPage/DeviceShare/DeviceGrantSelect';
import { BACK_BUTTON_ICON } from '../../LocationsPage/DeviceShare/DeviceShare';
import UsersWithAccess from '../../LocationsPage/DeviceShare/UsersWithAccess';
import { getLocationACLsToThemeResourceACLs } from '../utils';
import { selectDomainProfiles } from '../../../selectors/v2/domains';
import { changeV3DomainProfilesToV4 } from '../../LibraryPage/utils';
import {
  canDeleteResource,
  canEditResource,
  canShareResource,
} from '../../../utilities';

interface ThemeShareV2Props {
  theme: Theme;
  onClose: () => void;
}

export const THEME_GRANT_SELECT_OPTIONS: GrantOption[] = [
  {
    id: 'read',
    title: 'Viewer',
    description: 'View the theme',
    disabled: false,
  },
  {
    id: 'update',
    title: 'Editor',
    description: 'View and edit the theme',
    disabled: false,
  },
  {
    id: 'share',
    title: 'Access Manager',
    description: 'Editor rights plus share theme',
    disabled: false,
  },
  {
    id: 'delete',
    title: 'Theme Admin',
    description: 'All permissions, including delete theme',
    disabled: false,
  },
];

const ThemeShareV2 = ({ theme, onClose }: ThemeShareV2Props) => {
  const toastState = useToastState();
  const dispatch = useDispatch();

  const currentUser = useSelector(selectUserProfile);
  const domainProfiles = useSelector(selectDomainProfiles);

  const createResourceACL = useCreateResourceACLV5();
  const updateResourceACL = useUpdateResourceACLV5();
  const deleteResourceACL = useDeleteResourceACLV5();

  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [selectedGrant, setSelectedGrant] = useState<ResourceGrant>('read');
  const [shareWithDomain, setShareWithDomain] = useState(false);

  const v4DomainProfiles = useMemo(
    () => changeV3DomainProfilesToV4(domainProfiles),
    [domainProfiles],
  );

  const selectedUsers = useMemo(
    () => v4DomainProfiles.filter((user) => selectedUserIds.includes(user.id)),
    [v4DomainProfiles, selectedUserIds],
  );

  const availableGrants = useMemo(() => {
    if (!currentUser) return [];

    let defaultGrants = ['read'];

    if (canEditResource(currentUser, theme.resource)) {
      defaultGrants.push('update');
    }

    if (canShareResource(currentUser, theme.resource)) {
      defaultGrants.push('share');
    }

    if (canDeleteResource(currentUser, theme.resource)) {
      defaultGrants.push('delete');
    }

    return THEME_GRANT_SELECT_OPTIONS.map((grant) =>
      defaultGrants.includes(grant.id) ? grant : { ...grant, disabled: true },
    );
  }, [currentUser, theme.resource]);

  const updateThemeResourceACLs = useCallback(
    (resourceACLs: ResourceACL[]) => {
      if (!theme) return;
      dispatch(
        themeActions.updateThemeResourceACLs({
          themeId: theme.id,
          resourceACLs,
        }),
      );
    },
    [dispatch, theme],
  );

  const handleSelectUser = useCallback((value: string) => {
    if (value === 'domain') {
      setShareWithDomain(true);
      return;
    }
    setSelectedUserIds((prev) => {
      if (!prev.includes(value)) {
        return [...prev, value];
      }
      return prev;
    });
  }, []);

  const handleUnselectUser = useCallback((userId: string) => {
    setSelectedUserIds((prev) => prev.filter((id) => id !== userId));
  }, []);

  const handleReset = useCallback(() => {
    setSelectedUserIds([]);
    setSelectedGrant('read');
    setShareWithDomain(false);
  }, []);

  const handleShareContent = useCallback(async () => {
    if (!currentUser || !theme) return;

    try {
      const result = await createResourceACL.mutateAsync({
        params: {
          resourceId: theme.resource.id || '',
          grantProfileIds: !shareWithDomain ? selectedUserIds : [],
          grantDomainId: shareWithDomain ? currentUser?.domainId : '',
          grants: selectItemToGrants(selectedGrant),
        },
      });

      updateThemeResourceACLs(
        getLocationACLsToThemeResourceACLs(theme, result, currentUser),
      );

      handleReset();
      toastState.add({
        title: 'Theme Shared',
        description: `${theme?.name} shared successfully`,
      });
    } catch (error) {
      toastState.add({
        title: 'Could not share theme',
        description: 'Please try again',
        variant: 'caution',
      });
    }
  }, [
    createResourceACL,
    currentUser,
    handleReset,
    selectedGrant,
    selectedUserIds,
    shareWithDomain,
    theme,
    toastState,
    updateThemeResourceACLs,
  ]);

  const handleUpdateGrant = useCallback(
    async (acl: LocationACL, grant: ResourceGrant) => {
      if (!currentUser || !theme) return;
      try {
        const acls = await updateResourceACL.mutateAsync({
          params: {
            resourceId: theme?.resource?.id || '',
            grantProfileIds: acl.grantProfile.id ? [acl.grantProfile.id] : [],
            grantDomainId: acl.grantDomainId ? acl.grantDomainId : '',
            grants: selectItemToGrants(grant),
          },
        });
        updateThemeResourceACLs(
          getLocationACLsToThemeResourceACLs(theme, acls, currentUser),
        );
        handleReset();
        toastState.add({
          title: 'Grant Updated',
          description: 'Grant updated successfully',
        });
      } catch (error) {
        toastState.add({
          title: 'Could not update grant',
          description: 'Please try again',
          variant: 'caution',
        });
      }
    },
    [
      currentUser,
      handleReset,
      theme,
      toastState,
      updateResourceACL,
      updateThemeResourceACLs,
    ],
  );

  const handleUnShareContent = useCallback(
    async (acl: LocationACL) => {
      try {
        await deleteResourceACL.mutate({
          resourceId: theme?.resource?.id || '',
          params: {
            grantProfileIds: acl.grantProfile?.id ? [acl.grantProfile.id] : [],
            grantDomainId: acl.grantDomainId ? acl.grantDomainId : '',
          },
        });
        const updatedResourceACLs = theme?.resource?.r?.resourceACLs?.filter(
          (item) =>
            item.id !== acl.id || item.grantDomainId !== acl.grantDomainId,
        );

        updateThemeResourceACLs(updatedResourceACLs);

        handleReset();
        toastState.add({
          title: 'User Access Removed',
          description: 'Access removed successfully',
        });
      } catch (error) {
        toastState.add({
          title: 'Could not remove user access',
          description: 'Please try again',
          variant: 'caution',
        });
      }
    },
    [
      deleteResourceACL,
      handleReset,
      theme?.resource?.id,
      theme?.resource?.r?.resourceACLs,
      toastState,
      updateThemeResourceACLs,
    ],
  );

  const onGoBack = useCallback(() => {
    setSelectedUserIds([]);
    setSelectedGrant('read');
  }, []);

  const validUserList = useMemo(
    () =>
      v4DomainProfiles.filter(
        (user) =>
          user.id &&
          !selectedUserIds.includes(user.id) &&
          !theme.resource.r.resourceACLs.some(
            (acl) => acl.grantProfileId === user.id,
          ) &&
          user.id !== theme.resource.profile.id,
      ),
    [
      v4DomainProfiles,
      selectedUserIds,
      theme.resource.r.resourceACLs,
      theme.resource.profile.id,
    ],
  );

  const isContentSharedWithDomain = useMemo(
    () => theme.resource.r.resourceACLs.some((acl) => acl.grantDomainId),
    [theme.resource.r.resourceACLs],
  );

  const showSelectedUsers = shareWithDomain || selectedUserIds.length > 0;

  const renderSelectedUsers = () => (
    <div className="flex gap-2 flex-wrap pt-2">
      {shareWithDomain && (
        <Pill
          className="text-lg"
          icon={<User size={16} />}
          showCloseIcon
          onClick={() => setShareWithDomain(false)}
          label="Everyone at domain"
        />
      )}
      {!shareWithDomain &&
        selectedUsers.map((user) => (
          <Pill
            key={user.id}
            className="text-lg"
            icon={<User size={16} />}
            showCloseIcon
            onClick={() => handleUnselectUser(user.id)}
            label={user.name}
          />
        ))}
    </div>
  );

  if (!currentUser || !theme) return null;

  const validLocationACLs = theme.resource.r.resourceACLs.reduce(
    (acc, resourceACL) => {
      if (resourceACL.r?.grantProfile?.id || resourceACL.grantDomainId) {
        acc.push(getResourceACLToLocationACL(resourceACL));
      }
      return acc;
    },
    [] as ReturnType<typeof getResourceACLToLocationACL>[],
  );

  return (
    <Modal isOpen={!!theme} className="text-black overflow-auto">
      <Dialog showCloseButton onClose={onClose}>
        <DialogHeader>
          <div className="flex gap-4 items-start">
            {showSelectedUsers && (
              <Button variant="unstyled" onPress={onGoBack} className="mt-1">
                {BACK_BUTTON_ICON}
              </Button>
            )}
            <div>
              <DialogHeading>
                <span>Share '{theme.name}' theme</span>
              </DialogHeading>
            </div>
          </div>
        </DialogHeader>

        <div>
          <div className="flex flex-col">
            <div className="pt-4 pb-6">
              <ComboBox
                onSelectionChange={(key) => handleSelectUser(key as string)}
                selectedKey={''}
                placeholder="Select users"
                isDisabled={shareWithDomain || validUserList.length === 0}
              >
                {!isContentSharedWithDomain && (
                  <ComboBoxItem id="domain" key="domain" textValue="domain">
                    Everyone at domain
                  </ComboBoxItem>
                )}
                {validUserList.map((user) => (
                  <ComboBoxItem
                    key={user.id}
                    id={user.id}
                    textValue={user.name}
                  >
                    {user.name}
                  </ComboBoxItem>
                ))}
              </ComboBox>
              {renderSelectedUsers()}
            </div>

            {!showSelectedUsers ? (
              <div>
                <UsersWithAccess
                  owner={theme.resource?.profile}
                  contentType="theme"
                  grantOptions={THEME_GRANT_SELECT_OPTIONS}
                  locationACLs={validLocationACLs}
                  domainUsers={v4DomainProfiles}
                  onRemove={handleUnShareContent}
                  onUpdate={handleUpdateGrant}
                />
                <div className="mt-8 md:px-6 pb-4">
                  <Button onPress={onClose} variant="primary" fullWidth>
                    Done
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col">
                <ComboBox
                  label="Access Level"
                  onSelectionChange={(key) =>
                    setSelectedGrant(key as ResourceGrant)
                  }
                  selectedKey={selectedGrant}
                  isReadOnly
                >
                  {availableGrants.map((grant) => (
                    <ComboBoxItem
                      key={grant.id}
                      id={grant.id}
                      textValue={grant.title}
                      isDisabled={grant.disabled}
                      checkMarkSelected
                    >
                      <div className="cursor-pointer">
                        <div>{grant.title}</div>
                        <div className="text-sm text-gray-600">
                          {grant.description}
                        </div>
                      </div>
                    </ComboBoxItem>
                  ))}
                </ComboBox>
                <div className="mt-8 md:px-4 flex pb-4 gap-6">
                  <Button onPress={onClose} variant="secondary" fullWidth>
                    Cancel
                  </Button>
                  <Button
                    isDisabled={!showSelectedUsers}
                    onPress={handleShareContent}
                    variant="primary"
                    fullWidth
                  >
                    Share Now
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </Dialog>
    </Modal>
  );
};

export default ThemeShareV2;
